import { buildPermission } from "portal/utils/auth";
import { Button, LinearProgress } from "@mui/material";
import { capitalize, titleCase } from "portal/utils/strings";
import { CarbonDataGrid } from "portal/components/CarbonDataGrid";
import { CustomerResponse } from "protos/portal/customers";
import { CustomerScopeSelector } from "portal/components/customers/CustomerScopeSelector";
import { DEBOUNCE_MS } from "portal/utils/timing";
import { Farm } from "protos/farm/farm";
import { farmApi } from "common/state/portalApi/farm";
import { getFarmPath } from "portal/utils/routing";
import { GridColDef } from "@mui/x-data-grid-premium";
import { Header } from "portal/components/header/Header";
import { NoScroll, Page } from "portal/components/Page";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { portalApi } from "portal/state/portalApi";
import { SearchField } from "portal/components/header/SearchField";
import { useAuthorizationRequired } from "portal/components/auth/WithAuthorizationRequired";
import { useCanSeeOtherCustomers } from "portal/views/customers/permissions";
import { useCustomerScopeFilter } from "portal/components/customers/useCustomerScopeFilter";
import { useDebounce } from "@uidotdev/usehooks";
import { useNavigate } from "react-router-dom";
import { useNewFarmDialog } from "./NewFarmDialog";
import { useQueryPopups } from "portal/utils/hooks/useApiPopups";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import ImportIcon from "@mui/icons-material/FolderZip";
import React, { FunctionComponent, useMemo, useState } from "react";

const getRowId = (farm: Farm): string => {
  return farm.id?.id ?? "";
};

export const FarmList: FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [searchText, setSearchText] = useState<string>("");
  const search = useDebounce(searchText.trim(), DEBOUNCE_MS);
  const { customerUuid } = useCustomerScopeFilter();

  const {
    data: farmPages,
    isFetching,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useQueryPopups(
    farmApi.endpoints.listInfiniteFarms.useInfiniteQuery({
      contents: "none",
      search: search || undefined,
      customerUuid,
    })
  );
  const farms = useMemo(
    () => farmPages?.pages.flatMap((page) => page.farms),
    [farmPages]
  );

  const canCreateFarm = useAuthorizationRequired({
    or: [
      buildPermission(
        PermissionAction.update,
        PermissionResource.farms,
        PermissionDomain.customer
      ),
      buildPermission(
        PermissionAction.update,
        PermissionResource.farms,
        PermissionDomain.all
      ),
    ],
  });
  const canReadCustomers = useCanSeeOtherCustomers();

  const { data: allCustomers } = useQueryPopups(
    portalApi.endpoints.listCustomers.useQuery(undefined, {
      skip: !canReadCustomers,
    })
  );
  const { renderDialog: newFarmDialog, open: openNewFarmDialog } =
    useNewFarmDialog();

  const customersById = useMemo(() => {
    const result = new Map<number, CustomerResponse>();
    for (const customer of allCustomers ?? []) {
      const id = customer.db?.id;
      if (id !== undefined) {
        result.set(id, customer);
      }
    }
    return result;
  }, [allCustomers]);

  const columns = useMemo<GridColDef<Farm>[]>(() => {
    const defaultColumn: Partial<GridColDef> = {
      disableColumnMenu: true,
      flex: 1,
      sortable: false,
    };

    const resultColumns = [
      {
        ...defaultColumn,
        field: "name",
        headerName: capitalize(t("utils.descriptors.name")),
      },
    ];

    if (canReadCustomers) {
      resultColumns.push({
        ...defaultColumn,
        field: "customerId",
        headerName: capitalize(t("models.customers.customer_one")),
        valueFormatter: (customerId: number) =>
          customersById.get(customerId)?.name ?? t("utils.descriptors.unknown"),
      });
    }

    return resultColumns;
  }, [t, customersById, canReadCustomers]);

  return (
    <>
      <Header title={capitalize(t("models.farms.farm_other"))}>
        <CustomerScopeSelector />
        {canCreateFarm && (
          <>
            {newFarmDialog()}
            <div className="ml-auto flex shrink-0 flex-row">
              <Button
                className="text-white ml-auto"
                startIcon={<ImportIcon />}
                onClick={() => navigate("/farms/import-shapefiles")}
              >
                <span className="hidden sm:inline">
                  {t("views.farms.importFarms.buttonLong")}
                </span>
                <span className="sm:hidden">
                  {t("views.farms.importFarms.button")}
                </span>
              </Button>
              <Button
                className="text-white ml-auto"
                startIcon={<AddIcon />}
                onClick={openNewFarmDialog}
              >
                <span className="hidden sm:inline">
                  {titleCase(
                    t("utils.actions.newLong", {
                      subject: t("models.farms.farm_one"),
                    })
                  )}
                </span>
                <span className="sm:hidden">{t("utils.actions.new")}</span>
              </Button>
            </div>
          </>
        )}
      </Header>
      <Page>
        <NoScroll>
          <CarbonDataGrid<Farm>
            columns={columns}
            rows={farms ?? []}
            loading={isFetching && !isFetchingNextPage}
            getRowId={getRowId}
            getRowClassName={() => "cursor-pointer"}
            onRowClick={({ row: farm }) => navigate(getFarmPath(farm.id?.id))}
            disableRowSelectionOnClick
            hideFooter
            onRowsScrollEnd={() => {
              if (hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
              }
            }}
            header={
              <SearchField
                value={searchText}
                onChange={setSearchText}
                label={t("views.farms.searchFarms")}
              />
            }
          />
          <LinearProgress
            className={isFetchingNextPage ? undefined : "invisible"}
          />
        </NoScroll>
      </Page>
    </>
  );
};
